import { useCallback, useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { LightBulbIcon, MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import Button from "../common/Button";
import Dropdown from "../Settings/Dropdown";
import { useLocalStorageState } from "../../hooks/useLocalStorageState";
import { api, CATEGORY_LABELS, CATEGORY_ORDER, FeatureRequestItem, Sort, SORT_LABELS, STATUS_META, STATUS_ORDER } from "./api";
import BoardLayout from "./BoardLayout";
import RequestCard from "./RequestCard";
import { useVoteToggle } from "./useVoteToggle";

const FeatureRequestsBoard: React.FC = () => {
  const [status, setStatus] = useLocalStorageState<string>("featureRequests.status", "");
  const [category, setCategory] = useLocalStorageState<string>("featureRequests.category", "");
  const [sort, setSort] = useLocalStorageState<Sort>("featureRequests.sort", "top");
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [items, setItems] = useState<FeatureRequestItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Debounce the search box into the query the server sees.
  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(
    async (nextPage: number) => {
      nextPage === 0 ? setLoading(true) : setLoadingMore(true);
      try {
        const r = await api.list({ status: status || undefined, category: category || undefined, sort, q: q || undefined, page: nextPage });
        setItems((prev) => (nextPage === 0 ? r.items : [...prev, ...r.items]));
        setHasMore(r.hasMore);
        setPage(nextPage);
      } catch (e: any) {
        toast.error(e?.response?.data?.error || "Failed to load feature requests");
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [status, category, sort, q]
  );

  useEffect(() => {
    load(0);
  }, [load]);

  const { toggle, votingId } = useVoteToggle((id, patch) => setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i))));

  return (
    <BoardLayout>
      <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[12rem]">
          <MagnifyingGlassIcon className="h-4 w-4 text-base-content/40 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search requests..." className="w-full pl-9 pr-8 py-2 text-sm bg-base-100 border border-base-300 rounded-lg outline-none focus:border-primary/50 transition-colors" />
          {search && (
            <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-base-content/40 hover:text-gray-600 dark:hover:text-base-content/70 transition-colors" title="Clear search">
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
        </div>
        <Dropdown size="sm" dense options={[{ value: "", label: "All statuses" }, ...STATUS_ORDER.map((s) => ({ value: s, label: STATUS_META[s].label }))]} value={status} onChange={setStatus} />
        <Dropdown size="sm" dense options={[{ value: "", label: "All areas" }, ...CATEGORY_ORDER.map((c) => ({ value: c, label: CATEGORY_LABELS[c] }))]} value={category} onChange={setCategory} />
        <Dropdown size="sm" dense options={(Object.keys(SORT_LABELS) as Sort[]).map((s) => ({ value: s, label: `Sort: ${SORT_LABELS[s]}` }))} value={sort} onChange={(v) => setSort(v as Sort)} />
      </div>

      {loading ? (
        <div className="animate-pulse space-y-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-24 rounded-lg bg-base-300" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-base-300 bg-base-100 p-12 text-center">
          <LightBulbIcon className="size-10 mx-auto text-base-content/30 mb-3" />
          <p className="font-medium text-base-content">{q || status || category ? "No requests match" : "No requests yet"}</p>
          <p className="text-sm text-base-content/60">{q || status || category ? "Try a different search or filter." : "Be the first to ask for something."}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item, i) => (
            <RequestCard key={item.id} item={item} index={i} onVote={toggle} voting={votingId === item.id} />
          ))}
          {hasMore && (
            <div className="pt-2 text-center">
              <Button variant="secondary" size="sm" onClick={() => load(page + 1)} disabled={loadingMore}>
                {loadingMore ? "Loading…" : "Load more"}
              </Button>
            </div>
          )}
        </div>
      )}
      </div>
    </BoardLayout>
  );
};

export default FeatureRequestsBoard;
